import { useMemo, useState } from "react";
import {
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { it as itLocale } from "date-fns/locale";
import { ChevronLeft, ChevronRight, GripVertical, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { postponeOccurrence } from "@/lib/api";
import { EUR, fmtDate, num, recurrenceIT } from "@/lib/format";

const WEEKDAY_LABELS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

const todayISO = () => new Date().toISOString().slice(0, 10);

const isoFromDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;

const isOverdue = (occ) =>
  (occ.status === "planned" || occ.status === "partially_paid") &&
  occ.due_date < todayISO();

/** Locked statuses cannot be drag-rescheduled. */
const isDraggable = (occ) =>
  occ.status === "planned" || occ.status === "partially_paid";

/**
 * Month-grid calendar for occurrences.
 *
 * Props:
 *   rows           occurrences already filtered upstream
 *   expenses       map of expenseId → expense (for description / recurrence)
 *   vendors        map of vendorId → vendor
 *   categories     map of categoryId → category
 *   initialMonth   ISO date string (yyyy-MM-dd) used to anchor the visible month
 *   onChanged      callback fired after a successful drag-reschedule (for parent reload)
 */
export function OccurrencesCalendar({
  rows,
  expenses,
  vendors,
  categories,
  initialMonth,
  onChanged,
}) {
  const initialAnchor = useMemo(() => {
    const d = initialMonth ? parseISO(initialMonth) : new Date();
    return startOfMonth(d);
  }, [initialMonth]);

  const [anchor, setAnchor] = useState(initialAnchor);
  const [openDay, setOpenDay] = useState(null);
  // Drag-and-drop reschedule state
  const [draggingId, setDraggingId] = useState(null);
  const [hoverDay, setHoverDay] = useState(null);
  const [rescheduleProposal, setRescheduleProposal] = useState(null);
  const [confirming, setConfirming] = useState(false);

  // Build an occurrence map so we can look up the dropped item by id quickly.
  const occMap = useMemo(() => {
    const m = new Map();
    for (const r of rows || []) m.set(r.id, r);
    return m;
  }, [rows]);

  const enrich = (r) => {
    if (!r) return null;
    const exp = expenses[r.expense_id];
    const venId = r.vendor_id_override || exp?.vendor_id;
    const catId = r.category_id_override || exp?.category_id;
    return {
      ...r,
      title: exp?.title || "—",
      recurrence_type: exp?.recurrence_type,
      vendor_name: vendors[venId]?.name || "—",
      category_name: categories[catId]?.name || "—",
      postponed: r.original_due_date !== r.due_date,
      overdue: isOverdue(r),
    };
  };

  const handleDragStart = (e, occ) => {
    if (!isDraggable(occ)) {
      e.preventDefault();
      return;
    }
    e.stopPropagation();
    setDraggingId(occ.id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", occ.id);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setHoverDay(null);
  };

  const handleDayDragOver = (e, iso, occ) => {
    if (!draggingId) return;
    if (occ && occ.due_date === iso) return; // same day, no-op
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setHoverDay(iso);
  };

  const handleDayDrop = (e, iso) => {
    if (!draggingId) return;
    e.preventDefault();
    const occ = occMap.get(draggingId);
    setHoverDay(null);
    setDraggingId(null);
    if (!occ || occ.due_date === iso) return;
    setRescheduleProposal({ occurrence: enrich(occ), newDate: iso });
  };

  const cancelProposal = () => {
    if (confirming) return;
    setRescheduleProposal(null);
  };

  const confirmReschedule = async () => {
    if (!rescheduleProposal) return;
    setConfirming(true);
    try {
      await postponeOccurrence(rescheduleProposal.occurrence.id, {
        new_due_date: rescheduleProposal.newDate,
      });
      toast.success(
        `Scadenza rinviata al ${fmtDate(rescheduleProposal.newDate)}`,
      );
      setRescheduleProposal(null);
      if (typeof onChanged === "function") onChanged();
    } catch (err) {
      console.error(err);
      toast.error("Errore nel rinvio. Verificare la data e riprovare.");
    } finally {
      setConfirming(false);
    }
  };

  const grid = useMemo(() => {
    const monthStart = startOfMonth(anchor);
    const monthEnd = endOfMonth(anchor);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const days = [];
    for (
      let cursor = gridStart;
      cursor <= gridEnd;
      cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1)
    ) {
      days.push(new Date(cursor));
    }
    return days;
  }, [anchor]);

  const byDay = useMemo(() => {
    const map = new Map();
    for (const r of rows || []) {
      const list = map.get(r.due_date) || [];
      list.push(r);
      map.set(r.due_date, list);
    }
    return map;
  }, [rows]);

  const monthLabel = useMemo(
    () =>
      format(anchor, "MMMM yyyy", { locale: itLocale }).replace(/^./, (c) =>
        c.toUpperCase(),
      ),
    [anchor],
  );

  const dayDetails = useMemo(() => {
    if (!openDay) return null;
    const list = (byDay.get(openDay) || []).map((r) => enrich(r));
    const totals = list.reduce(
      (acc, r) => ({
        planned: acc.planned + num(r.effective_amount),
        paid: acc.paid + num(r.total_paid),
        count: acc.count + 1,
      }),
      { planned: 0, paid: 0, count: 0 },
    );
    return { list, totals };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDay, byDay, expenses, vendors, categories]);

  return (
    <div data-testid="occurrences-calendar" className="rounded-xl border border-stone-200 bg-white">
      <div className="flex items-center justify-between border-b border-stone-200 px-5 py-3">
        <Button
          variant="ghost"
          size="sm"
          data-testid="calendar-prev-month"
          onClick={() => setAnchor((d) => addMonths(d, -1))}
        >
          <ChevronLeft size={16} strokeWidth={1.8} />
          Mese precedente
        </Button>
        <div
          data-testid="calendar-month-label"
          className="text-sm font-semibold uppercase tracking-wider text-stone-700"
        >
          {monthLabel}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            data-testid="calendar-today"
            onClick={() => setAnchor(startOfMonth(new Date()))}
          >
            Oggi
          </Button>
          <Button
            variant="ghost"
            size="sm"
            data-testid="calendar-next-month"
            onClick={() => setAnchor((d) => addMonths(d, 1))}
          >
            Mese successivo
            <ChevronRight size={16} strokeWidth={1.8} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-7 border-b border-stone-200 bg-stone-50 text-[11px] font-medium uppercase tracking-wider text-stone-500">
        {WEEKDAY_LABELS.map((l) => (
          <div key={l} className="px-2 py-2 text-center">
            {l}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {grid.map((day) => {
          const iso = isoFromDate(day);
          const items = byDay.get(iso) || [];
          const planned = items.reduce((s, r) => s + num(r.effective_amount), 0);
          const paid = items.reduce((s, r) => s + num(r.total_paid), 0);
          const inMonth = isSameMonth(day, anchor);
          const isToday = isSameDay(day, new Date());
          const overdueCount = items.filter(isOverdue).length;
          const allPaid =
            items.length > 0 && items.every((r) => r.status === "paid");
          const partial = items.some((r) => r.status === "partially_paid");
          const postponed = items.some((r) => r.original_due_date !== r.due_date);

          let dotClass = "bg-stone-300";
          if (overdueCount > 0) dotClass = "bg-rose-500";
          else if (allPaid) dotClass = "bg-emerald-500";
          else if (partial) dotClass = "bg-amber-500";
          else if (items.length > 0) dotClass = "bg-stone-700";

          const isHover = hoverDay === iso && draggingId;
          const draggedSelf = items.some((r) => r.id === draggingId);
          const visibleChips = items.slice(0, 2);
          const moreCount = items.length - visibleChips.length;

          return (
            <div
              key={iso}
              data-testid={`calendar-day-${iso}`}
              role={items.length > 0 ? "button" : undefined}
              tabIndex={items.length > 0 ? 0 : -1}
              onClick={(e) => {
                if (e.defaultPrevented) return;
                if (items.length > 0) setOpenDay(iso);
              }}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && items.length > 0) {
                  e.preventDefault();
                  setOpenDay(iso);
                }
              }}
              onDragOver={(e) =>
                handleDayDragOver(e, iso, occMap.get(draggingId))
              }
              onDragLeave={() => setHoverDay((h) => (h === iso ? null : h))}
              onDrop={(e) => handleDayDrop(e, iso)}
              className={
                "flex h-32 flex-col items-stretch border-b border-r border-stone-100 px-2 py-1.5 text-left transition-colors " +
                (inMonth ? "bg-white" : "bg-stone-50/40 text-stone-400") +
                (items.length > 0
                  ? " cursor-pointer hover:bg-stone-50"
                  : " cursor-default") +
                (isToday ? " ring-2 ring-inset ring-stone-900/70" : "") +
                (isHover && !draggedSelf
                  ? " bg-blue-50/80 ring-2 ring-inset ring-blue-400"
                  : "")
              }
            >
              <div className="flex items-center justify-between">
                <span
                  className={
                    "text-xs font-medium " +
                    (isToday
                      ? "text-stone-900"
                      : inMonth
                      ? "text-stone-600"
                      : "text-stone-400")
                  }
                >
                  {day.getDate()}
                </span>
                {items.length > 0 && (
                  <span
                    className={
                      "inline-flex h-4 min-w-[20px] items-center justify-center rounded-full px-1 text-[10px] font-semibold text-white " +
                      dotClass
                    }
                  >
                    {items.length}
                  </span>
                )}
              </div>

              {visibleChips.length > 0 && (
                <div
                  className="mt-1 space-y-0.5"
                  onClick={(e) => e.stopPropagation()}
                >
                  {visibleChips.map((occ) => {
                    const exp = expenses[occ.expense_id];
                    const can = isDraggable(occ);
                    const overdue = isOverdue(occ);
                    const paidTone =
                      occ.status === "paid"
                        ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                        : occ.status === "partially_paid"
                        ? "border-amber-200 bg-amber-50 text-amber-800"
                        : overdue
                        ? "border-rose-200 bg-rose-50 text-rose-800"
                        : "border-stone-200 bg-stone-50 text-stone-700";
                    return (
                      <div
                        key={occ.id}
                        draggable={can}
                        onDragStart={(e) => handleDragStart(e, occ)}
                        onDragEnd={handleDragEnd}
                        onClick={(e) => {
                          // open detail without losing drag intent
                          e.stopPropagation();
                          setOpenDay(iso);
                        }}
                        title={
                          can
                            ? "Trascina su un altro giorno per rinviare"
                            : "Voce non rinviabile"
                        }
                        data-testid={`chip-${occ.id}`}
                        className={
                          "flex items-center gap-1 truncate rounded border px-1.5 py-0.5 text-[10px] " +
                          paidTone +
                          (can ? " cursor-grab active:cursor-grabbing" : " cursor-not-allowed opacity-90") +
                          (draggingId === occ.id ? " opacity-40" : "")
                        }
                      >
                        {can && (
                          <GripVertical
                            size={10}
                            strokeWidth={2}
                            className="shrink-0 text-stone-400"
                          />
                        )}
                        <span className="truncate font-medium">
                          {exp?.title || "—"}
                        </span>
                        <span className="ml-auto shrink-0 font-mono tabular-nums">
                          {EUR(occ.effective_amount)}
                        </span>
                      </div>
                    );
                  })}
                  {moreCount > 0 && (
                    <div className="text-[10px] text-stone-500">
                      +{moreCount} altre…
                    </div>
                  )}
                </div>
              )}

              {items.length > 0 && (
                <div className="mt-auto flex items-center justify-between gap-2 pt-1">
                  <div className="font-mono text-[11px] tabular-nums text-stone-700">
                    {EUR(planned)}
                  </div>
                  {overdueCount > 0 ? (
                    <div className="text-[10px] uppercase text-rose-700">
                      {overdueCount} scaduta{overdueCount > 1 ? "e" : ""}
                    </div>
                  ) : paid > 0 ? (
                    <div className="font-mono text-[10px] tabular-nums text-emerald-700">
                      ✓ {EUR(paid)}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-3 border-t border-stone-200 px-5 py-2.5 text-[11px] text-stone-500">
        <LegendDot color="bg-stone-700" label="In programma" />
        <LegendDot color="bg-amber-500" label="Parziale" />
        <LegendDot color="bg-emerald-500" label="Pagato" />
        <LegendDot color="bg-rose-500" label="Scaduto non saldato" />
        <span className="ml-auto">
          Trascina una scadenza su un altro giorno per rinviarla — sarà chiesta
          conferma
        </span>
      </div>

      <Dialog open={!!openDay} onOpenChange={(o) => !o && setOpenDay(null)}>
        <DialogContent className="max-w-3xl" data-testid="dialog-day-detail">
          <DialogHeader>
            <DialogTitle>
              Scadenze del {openDay ? fmtDate(openDay) : ""}
            </DialogTitle>
            {dayDetails && (
              <DialogDescription>
                {dayDetails.totals.count} scadenz
                {dayDetails.totals.count === 1 ? "a" : "e"} · pianificato{" "}
                <strong>{EUR(dayDetails.totals.planned)}</strong> · pagato{" "}
                <strong>{EUR(dayDetails.totals.paid)}</strong>
              </DialogDescription>
            )}
          </DialogHeader>
          {dayDetails && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Descrizione</TableHead>
                  <TableHead>Fornitore</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Ricorrenza</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="text-right">Pianificato</TableHead>
                  <TableHead className="text-right">Pagato</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dayDetails.list.map((r) => (
                  <TableRow
                    key={r.id}
                    data-testid={`day-row-${r.id}`}
                    className={r.overdue ? "bg-rose-50/60" : ""}
                  >
                    <TableCell>
                      <div className="text-sm font-medium text-stone-900">
                        {r.title}
                      </div>
                      {r.notes && (
                        <div className="text-xs text-stone-500">{r.notes}</div>
                      )}
                      {r.postponed && (
                        <div className="text-[10px] uppercase text-blue-700">
                          Rinviata da {fmtDate(r.original_due_date)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{r.vendor_name}</TableCell>
                    <TableCell className="text-xs text-stone-600">
                      {r.category_name}
                    </TableCell>
                    <TableCell className="text-xs text-stone-600">
                      {recurrenceIT[r.recurrence_type] || "—"}
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        status={r.status}
                        modified={r.is_modified}
                        postponed={r.postponed}
                      />
                      {r.overdue && (
                        <span className="ml-1 inline-flex rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10px] uppercase text-rose-700">
                          Scaduta
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {EUR(r.effective_amount)}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {EUR(r.total_paid)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Link
                        to={`/expenses/${r.expense_id}/edit`}
                        className="text-xs text-blue-700 hover:underline"
                      >
                        Serie
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              data-testid="day-detail-close"
              onClick={() => setOpenDay(null)}
            >
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Drag-and-drop reschedule confirmation */}
      <Dialog
        open={!!rescheduleProposal}
        onOpenChange={(o) => !o && cancelProposal()}
      >
        <DialogContent
          data-testid="dialog-reschedule-confirm"
          className="max-w-lg"
        >
          <DialogHeader>
            <DialogTitle>Confermare il rinvio?</DialogTitle>
            <DialogDescription>
              La cadenza della serie <strong>non si sposta</strong>: viene
              rinviata solo questa scadenza. Le scadenze future restano nelle
              date originali.
            </DialogDescription>
          </DialogHeader>

          {rescheduleProposal && (
            <div className="space-y-3 rounded-lg border border-stone-200 bg-stone-50/60 p-4 text-sm">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-stone-500">
                  Descrizione
                </div>
                <div
                  className="font-medium text-stone-900"
                  data-testid="reschedule-title"
                >
                  {rescheduleProposal.occurrence.title}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-stone-500">
                    Fornitore
                  </div>
                  <div data-testid="reschedule-vendor">
                    {rescheduleProposal.occurrence.vendor_name}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-stone-500">
                    Categoria
                  </div>
                  <div className="text-stone-700">
                    {rescheduleProposal.occurrence.category_name}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 border-t border-stone-200 pt-3">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-stone-500">
                    Data attuale
                  </div>
                  <div
                    className="font-mono text-base font-semibold text-stone-900 line-through decoration-stone-400"
                    data-testid="reschedule-from-date"
                  >
                    {fmtDate(rescheduleProposal.occurrence.due_date)}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-stone-500">
                    Nuova data
                  </div>
                  <div
                    className="font-mono text-base font-semibold text-blue-700"
                    data-testid="reschedule-to-date"
                  >
                    {fmtDate(rescheduleProposal.newDate)}
                  </div>
                </div>
              </div>

              <div className="border-t border-stone-200 pt-3">
                <div className="text-[11px] uppercase tracking-wider text-stone-500">
                  Importo pianificato
                </div>
                <div
                  className="font-mono text-lg font-semibold text-stone-900"
                  data-testid="reschedule-amount"
                >
                  {EUR(rescheduleProposal.occurrence.effective_amount)}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={cancelProposal}
              disabled={confirming}
              data-testid="reschedule-cancel"
            >
              Annulla
            </Button>
            <Button
              onClick={confirmReschedule}
              disabled={confirming}
              data-testid="reschedule-confirm"
            >
              {confirming ? (
                <>
                  <Loader2 className="mr-1.5 animate-spin" size={14} />
                  Salvataggio…
                </>
              ) : (
                "Conferma rinvio"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block h-2 w-2 rounded-full ${color}`} />
      {label}
    </span>
  );
}
